/* eslint-disable react-hooks/exhaustive-deps */
import {useEffect, useRef, useState} from 'react'
import {Box, Chip, CircularProgress, IconButton, Snackbar, Tooltip, Typography} from '@mui/material'
import {mdiCalendarClockOutline, mdiChevronLeft, mdiChevronRight, mdiPencilOutline} from '@mdi/js'
import Icon from '@mdi/react'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import Loader from 'src/components/elements/Loader/Loader'
import PrimaryButton from 'src/components/elements/Buttons/PrimaryButton/PrimaryButton'
import SnackbarAlertCustom from 'src/components/elements/SnackbarAlertCustom/SnackbarAlertCustom'
import {STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import {useAppSelector} from 'src/hooks'
import {
  ASSUMPTIONS_WINDOW_SIZE,
  canMoveSecondaryAssumption,
  canRespondToSecondaryAssumption,
  formatAssumptionTime,
  formatDayStripLabel,
  formatSecondaryAssumptionState,
  getAssumptionDayKey,
  getAssumptionSummaryParts,
  getDistinctAssumptionDays,
  getSecondaryAssumptionKey,
  getTodayDayKey,
  getAssumptionColumnOptions,
  getVisibleAssumptionFieldIds,
  isEmptyListStatus,
  isSecondaryAssumptionAnswered,
  pickDefaultAssumptionDay,
  SecondaryAssumption
} from 'src/constants/secondaryAssumptions'
import AssumptionResponseDialog from './AssumptionResponseDialog'
import MoveNextDoseDialog from './MoveNextDoseDialog'
import QuickAnswerButtons from './QuickAnswerButtons'

// Day-strip history of the reminders sent for ONE secondary therapy, mounted (per selected
// therapy) from the "Terapie Accessorie" tab of Diario Paziente
// (src/pages/therapyDiary/SecondaryAssumptionsDiaryPanel.tsx) — moved here from the therapy's own
// card on /therapies (see SecondaryTherapyCardTabs.tsx), aligning the legacy stack with the new one.
//
// Scoped to one secondary therapy because that is what RF3.1 allows (secondaryTherapyId is
// required).
//
// Navigated with a horizontal day-strip (default: today) rather than page numbers: RF3.1 has no
// date filter, so this loads a bounded WINDOW of the most recent reminders (ASSUMPTIONS_WINDOW_SIZE
// documents, not a fixed number of days) and groups it by calendar day client-side. A "Carica
// assunzioni più vecchie" action extends the window on demand.
//
// Each item deliberately does NOT use CardCustom (white background, bordered, same look as every
// other card on this page): this list sits inside a therapy's own CardCustom, and a white-bordered
// card nested in an identical white-bordered card is indistinguishable from it. Instead each item
// is a flat, tinted panel so it reads as content INSIDE the therapy card.

type SecondaryAssumptionsListProps = {
  secondaryTherapyId: string
}

const CHIP_COLORS: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  COMPLETED: 'success',
  PLANNED: 'warning',
  CANCELLED: 'error',
  NOT_ANSWERED: 'default'
}

const SecondaryAssumptionsList: React.FC<SecondaryAssumptionsListProps> = ({secondaryTherapyId}) => {
  const [assumptions, setAssumptions] = useState<SecondaryAssumption[]>([])
  const [status, setStatus] = useState<string | undefined>(undefined)
  const [hasMore, setHasMore] = useState<boolean>(false)
  const [loadingMore, setLoadingMore] = useState<boolean>(false)
  const [selectedDay, setSelectedDay] = useState<string | undefined>(undefined)
  const [respondingTo, setRespondingTo] = useState<SecondaryAssumption | undefined>(undefined)
  const [confirmingTaken, setConfirmingTaken] = useState<SecondaryAssumption | undefined>(undefined)
  const [denyingId, setDenyingId] = useState<string | undefined>(undefined)
  const [loadError, setLoadError] = useState<boolean>(false)
  const [denyError, setDenyError] = useState<boolean>(false)
  const [movingNextDose, setMovingNextDose] = useState<SecondaryAssumption | undefined>(undefined)
  const [moveError, setMoveError] = useState<boolean>(false)
  const assumptionsColumnOptions = useAppSelector(
    state => getAssumptionColumnOptions(state.pspConfigReducer.data)
  )
  const visibleAssumptionFields = getVisibleAssumptionFieldIds(assumptionsColumnOptions)
  const showTime = visibleAssumptionFields.has('time')
  const showStatus = visibleAssumptionFields.has('status')
  const stripRef = useRef<HTMLDivElement>(null)
  const hasAutoScrolledRef = useRef(false)

  const fetchInitial = async () => {
    setLoadError(false)
    // Bound to its own flag rather than to STATUS.REJECTED: a therapy with no reminders yet answers
    // 404, and the empty state below already says exactly that — a popup on top of it reads as a
    // failure when nothing failed. apiClientRequest swallows the rejection, so `onError` is the
    // only place the status code surfaces.
    const result = await apiClient.getSecondaryAssumptions(
      {secondaryTherapyId, _l: ASSUMPTIONS_WINDOW_SIZE, _sk: 0},
      setStatus,
      (_message: string | undefined, httpStatus: number | undefined) => {
        if (!isEmptyListStatus(httpStatus)) { setLoadError(true) }
      }
    )
    const list = Array.isArray(result) ? result : []
    setAssumptions(list)
    setHasMore(list.length === ASSUMPTIONS_WINDOW_SIZE)
    setSelectedDay(pickDefaultAssumptionDay(getDistinctAssumptionDays(list)))
  }

  useEffect(() => {
    fetchInitial()
  }, [secondaryTherapyId])

  const days = getDistinctAssumptionDays(assumptions)

  // Scrolls the strip so the selected (usually most recent) day is in view the first time it has
  // something to show. Only once — "Carica assunzioni più vecchie" adds days without yanking the
  // scroll position away from whatever the user is currently browsing.
  useEffect(() => {
    if (!hasAutoScrolledRef.current && days.length > 0 && stripRef.current) {
      stripRef.current.scrollTo({left: stripRef.current.scrollWidth})
      hasAutoScrolledRef.current = true
    }
  }, [days.length])

  // Fetches the next window past what is already loaded and returns the full, appended list — the
  // one fetch both "Carica più vecchie" and the ← arrow build on, so extending the window and
  // stepping to an earlier day never duplicate the request logic.
  const fetchMore = async (): Promise<SecondaryAssumption[]> => {
    const more = await apiClient.getSecondaryAssumptions(
      {secondaryTherapyId, _l: ASSUMPTIONS_WINDOW_SIZE, _sk: assumptions.length},
      undefined
    )
    const list = Array.isArray(more) ? more : []
    const next = [...assumptions, ...list]
    setAssumptions(next)
    setHasMore(list.length === ASSUMPTIONS_WINDOW_SIZE)
    return next
  }

  const loadOlder = async () => {
    setLoadingMore(true)
    try {
      await fetchMore()
    } catch {
      setLoadError(true)
    } finally {
      setLoadingMore(false)
    }
  }

  // ←/→ step the day-strip selection by one calendar day. Forward never needs a fetch — the most
  // recent day is always in the very first page, since the API sorts newest-first — but stepping
  // back past the oldest LOADED day may need to reach further into history than the current
  // window covers, so it extends the window first (RF3.1 has no date filter to jump straight
  // there). If that pull doesn't surface an even older day — several reminders can share one day
  // on a frequent therapy — the arrow stays put and pressing it again pulls the next chunk.
  const goToPreviousDay = async () => {
    const idx = days.indexOf(selectedDay ?? '')
    if (idx > 0) {
      setSelectedDay(days[idx - 1])
      return
    }
    if (!hasMore) { return }
    setLoadingMore(true)
    try {
      const next = await fetchMore()
      const nextDays = getDistinctAssumptionDays(next)
      const nextIdx = nextDays.indexOf(selectedDay ?? '')
      if (nextIdx > 0) { setSelectedDay(nextDays[nextIdx - 1]) }
    } catch {
      setLoadError(true)
    } finally {
      setLoadingMore(false)
    }
  }

  const goToNextDay = () => {
    const idx = days.indexOf(selectedDay ?? '')
    if (idx >= 0 && idx < days.length - 1) {
      setSelectedDay(days[idx + 1])
    }
  }

  // Answering never adds/removes a document or changes its day — only its own state/takenAt/
  // bodyPart/respondedAt — so the already-loaded window is patched in place instead of refetched.
  // Matched against the key the row had BEFORE the patch (`original`), not against `updated._id`:
  // the derived row (no `_id` yet, RF1.4) turns into a persisted one once answered, so its `_id`
  // changes from null to a real one and can no longer be used to find it back in the list.
  const patchLocal = (original?: SecondaryAssumption, updated?: SecondaryAssumption) => {
    if (!original || !updated) { return }
    const key = getSecondaryAssumptionKey(original)
    setAssumptions(prev => prev.map(a => (getSecondaryAssumptionKey(a) === key ? {...a, ...updated} : a)))
  }

  // RF4/RF1.2: a CANCELLED answer has nothing else to ask (no takenAt, no bodyPart), so the round
  // ✗ button submits straight away instead of opening a dialog for a form with only one field.
  // No `setStatus` is passed to the api call, so a failure rejects instead of resolving to
  // undefined — caught here directly since this action has no status of its own to render.
  const onQuickDeny = async (assumption: SecondaryAssumption) => {
    const key = getSecondaryAssumptionKey(assumption)
    setDenyingId(key)
    try {
      // RF4.3: the derived "next dose" row (no _id yet) is addressed by natural key instead.
      const updated = assumption._id
        ? await apiClient.patchSecondaryAssumption(assumption._id, {state: 'CANCELLED'}, undefined)
        : await apiClient.patchSecondaryAssumptionByKey(
          {secondaryTherapyId: assumption.secondaryTherapyId, expectedDate: assumption.expectedDate as string},
          {state: 'CANCELLED'},
          undefined
        )
      patchLocal(assumption, updated)
    } catch {
      setDenyError(true)
    } finally {
      setDenyingId(undefined)
    }
  }

  if (status === STATUS.PENDING) {
    return <Loader />
  }

  if (assumptions.length === 0) {
    return (
      <Typography variant='body2' color='textSecondary'>
        Nessun promemoria inviato per questa terapia.
      </Typography>
    )
  }

  const todayKey = getTodayDayKey()
  const dayAssumptions = assumptions
    .filter(a => getAssumptionDayKey(a) === selectedDay)
    // Newest reminder of the day first — matches the list's overall reverse-chronological order.
    .sort((a, b) => (b.notificationSentAt ?? '').localeCompare(a.notificationSentAt ?? ''))

  return (
    <>
      <div style={{display: 'flex', alignItems: 'center', gap: 4, marginBottom: 16}}>
        <IconButton
          aria-label='Giorno precedente'
          disabled={days.indexOf(selectedDay ?? '') === 0 && !hasMore}
          onClick={goToPreviousDay}
          size='small'
        >
          {loadingMore ? <CircularProgress size={16} /> : <Icon path={mdiChevronLeft} size={0.8} />}
        </IconButton>

        <div ref={stripRef} style={{display: 'flex', gap: 8, overflowX: 'auto', padding: '4px 0'}}>
          {days.map(day => {
            const {dayNumber, weekday} = formatDayStripLabel(day)
            const isSelected = day === selectedDay
            return (
              <Box
                key={day}
                onClick={() => setSelectedDay(day)}
                sx={{
                  flexShrink: 0,
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  backgroundColor: isSelected ? 'primary.main' : 'grey.50',
                  color: isSelected ? '#fff' : 'text.primary',
                  border: day === todayKey && !isSelected ? '1px solid' : 'none',
                  borderColor: 'info.main',
                }}
              >
                <Typography sx={{fontSize: 15, fontWeight: 700, lineHeight: 1.2}}>{dayNumber}</Typography>
                <Typography sx={{fontSize: 9, textTransform: 'capitalize', opacity: isSelected ? 0.9 : 0.7, lineHeight: 1.2}}>
                  {weekday}
                </Typography>
              </Box>
            )
          })}
        </div>

        <IconButton
          aria-label='Giorno successivo'
          disabled={days.indexOf(selectedDay ?? '') >= days.length - 1}
          onClick={goToNextDay}
          size='small'
        >
          <Icon path={mdiChevronRight} size={0.8} />
        </IconButton>
      </div>

      <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
        {dayAssumptions.map(assumption => {
          const timeLabel = showTime ? formatAssumptionTime(assumption.expectedDate) : ''
          const answered = isSecondaryAssumptionAnswered(assumption)
          const summaryParts = getAssumptionSummaryParts(assumption, visibleAssumptionFields)

          return (
            <Box
              key={getSecondaryAssumptionKey(assumption)}
              sx={{
                borderRadius: '8px',
                // PLANNED rows keep the warning tint (they need attention); the rest fall back to
                // a plain light gray, still visibly distinct from the therapy card's white behind it.
                backgroundColor: assumption.state === 'PLANNED' ? '#FEF9C3' : 'grey.50',
                px: 1.5,
                py: 1,
              }}
            >
              {/* The action sits next to the state chip on the SAME row rather than below on a row
                  of its own: with no summary line (the common case — see below), a separate action
                  row was the only thing left in the card besides the header, i.e. a whole empty
                  band reserved just to hold one small icon/button pair. */}
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap'}}>
                {/* Time only, not the full date: the day is already selected via the strip above. */}
                {showTime && (
                  timeLabel ? (
                    <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: 5, backgroundColor: '#fff'}}>
                      <AccessTimeIcon sx={{fontSize: 14, color: 'grey.600'}} />
                      <Typography variant='body2' style={{fontWeight: 600}}>{timeLabel}</Typography>
                    </Box>
                  ) : (
                    <Typography variant='body2' color='textSecondary'>Orario non disponibile</Typography>
                  )
                )}
                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                  {showStatus && (
                    <Chip
                      size='small'
                      label={formatSecondaryAssumptionState(assumption.state)}
                      color={CHIP_COLORS[assumption.state ?? ''] ?? 'default'}
                    />
                  )}
                  {/* Every row stays actionable, including one closed as NOT_ANSWERED: an answer
                      has no deadline and no single use (RF4.2). An unanswered row gets the round
                      quick-answer buttons; an already-answered one keeps the edit icon —
                      correcting it may flip the yes/no answer itself, so it needs the full form. */}
                  {canRespondToSecondaryAssumption(assumption) && (
                    answered ? (
                      <Tooltip title='Modifica'>
                        <IconButton color='primary' onClick={() => setRespondingTo(assumption)} size='small'>
                          <Icon path={mdiPencilOutline} size={0.8} />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <QuickAnswerButtons
                        denyLoading={denyingId === getSecondaryAssumptionKey(assumption)}
                        onDeny={() => onQuickDeny(assumption)}
                        onConfirm={() => setConfirmingTaken(assumption)}
                      />
                    )
                  )}
                  {/* RF4.5: only the derived next dose, while still ahead, can be moved */}
                  {canMoveSecondaryAssumption(assumption) && (
                    <Tooltip title='Sposta prossima assunzione'>
                      <IconButton color='primary' onClick={() => setMovingNextDose(assumption)} size='small'>
                        <Icon path={mdiCalendarClockOutline} size={0.8} />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              </div>

              {/* Only rendered when there is something to say beyond the badge/chip above — the
                  common "taken as planned"/"cancelled"/"not answered" cases add nothing here. */}
              {summaryParts.length > 0 && (
                <Typography variant='body2' color='textSecondary' style={{marginTop: 8}}>
                  {summaryParts.join(' · ')}
                </Typography>
              )}
            </Box>
          )
        })}
      </div>

      {hasMore && (
        <div style={{display: 'flex', justifyContent: 'center', paddingTop: 16}}>
          <PrimaryButton disabled={loadingMore} onClick={loadOlder}>
            Carica assunzioni più vecchie
          </PrimaryButton>
        </div>
      )}

      <AssumptionResponseDialog
        open={Boolean(respondingTo)}
        assumption={respondingTo}
        onClose={() => setRespondingTo(undefined)}
        onSuccess={updated => {
          setRespondingTo(undefined)
          patchLocal(respondingTo, updated)
        }}
      />

      <AssumptionResponseDialog
        open={Boolean(confirmingTaken)}
        assumption={confirmingTaken}
        presetTaken='yes'
        onClose={() => setConfirmingTaken(undefined)}
        onSuccess={updated => {
          setConfirmingTaken(undefined)
          patchLocal(confirmingTaken, updated)
        }}
      />

      <MoveNextDoseDialog
        open={Boolean(movingNextDose)}
        assumption={movingNextDose}
        onClose={() => setMovingNextDose(undefined)}
        onSuccess={expectedDate => {
          // RF4.5: nothing is persisted, the derived row just gets its new time (and natural key) —
          // and the strip follows it, since the new time may fall on another day
          patchLocal(movingNextDose, movingNextDose && {...movingNextDose, expectedDate})
          setSelectedDay(getAssumptionDayKey({...movingNextDose, expectedDate} as SecondaryAssumption))
          setMovingNextDose(undefined)
        }}
        onError={() => {
          setMovingNextDose(undefined)
          setMoveError(true)
          fetchInitial()
        }}
      />

      <Snackbar
        open={moveError}
        autoHideDuration={4000}
        onClose={() => setMoveError(false)}
      >
        <SnackbarAlertCustom severity='error' onClose={() => setMoveError(false)}>
          Impossibile spostare la prossima assunzione. La lista è stata aggiornata, riprova.
        </SnackbarAlertCustom>
      </Snackbar>

      <Snackbar
        open={loadError}
        autoHideDuration={4000}
        onClose={() => setLoadError(false)}
      >
        <SnackbarAlertCustom severity='error' onClose={() => setLoadError(false)}>
          Impossibile caricare le assunzioni.
        </SnackbarAlertCustom>
      </Snackbar>

      <Snackbar
        open={denyError}
        autoHideDuration={4000}
        onClose={() => setDenyError(false)}
      >
        <SnackbarAlertCustom severity='error' onClose={() => setDenyError(false)}>
          Impossibile registrare la risposta. Riprova.
        </SnackbarAlertCustom>
      </Snackbar>
    </>
  )
}

export default SecondaryAssumptionsList
